import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { borderRadius, customSizes, palette, spaces } from '../../assets/styles/themes/tokens';
import { useTranslation } from 'react-i18next';
import { PlanKey } from '@kimuno/api-sdk/lib/types/requests/Billing';
import { STRIPE_PLAN_DISPLAY } from '../../constants/stripePlanBenefits';
import StripePlanBenefits from './StripePlanBenefits';

interface StripeCurrentPlanCardProps {
  currentPlanKey: PlanKey;
}

// "Dein momentanes Paket" card — leftmost block of the Stripe upgrade row.
// Visually mirrors the existing `PricingPackage` component for the Zuora flow,
// duplicated here so the Stripe path can evolve independently without touching
// the public pricing page.
const StripeCurrentPlanCard: React.FC<StripeCurrentPlanCardProps> = ({ currentPlanKey }) => {
  const { t } = useTranslation();
  const display = STRIPE_PLAN_DISPLAY[currentPlanKey];

  return (
    <Box
      data-testid='stripe-current-plan-card'
      sx={{
        width: customSizes.pricingPackage.width,
        display: 'flex',
        flexDirection: 'column',
        gap: spaces.xl,
        border: `1px solid ${palette.primary.main}`,
        borderRadius: borderRadius.xl,
        backgroundColor: palette.common.white,
        position: 'relative',
        padding: spaces.lg,
      }}
    >
      <Chip
        label={t('Your current plan')}
        color='primary'
        size='medium'
        variant='filled'
        data-testid='stripe-current-plan-chip'
        sx={{
          height: 34,
          position: 'absolute',
          top: '-18px',
          left: '50%',
          transform: 'translateX(-50%)',
          '.MuiChip-label': { fontWeight: 500 },
        }}
      />
      <Box
        data-testid='stripe-current-plan-header'
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          height: '80px',
        }}
      >
        <Typography
          variant='h4'
          color={palette.text.primary}
          sx={{ fontWeight: 600 }}
        >
          {t(display.packageName)}
        </Typography>
        <Typography variant='body2' color={palette.text.secondary}>
          {t(display.description)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.xl,
        }}
      >
        {display.fullBenefits.map((section, index) => (
          <StripePlanBenefits key={index} benefit={section} />
        ))}
      </Box>
    </Box>
  );
};

export default StripeCurrentPlanCard;
